/**
 * FirstRunSetup — the first-run setup flow (T30.2).
 *
 * A short two-step first run for a new household manager: capture the
 * household's LOCATION (step 1), then discover and pick the household's
 * SELECTED STORES (step 2), then land on the active list. It reuses the
 * existing settings building blocks rather than re-implementing the writers:
 *
 *   - Step 1 reuses `LocationCard` — address geocode + "Use my current
 *     location" GPS, writing through `PATCH /api/household`
 *     (`useUpdateCurrentHousehold`). The backend auto-fires store discovery on
 *     the first coordinate save and returns `discoveredStores`; the card hands
 *     those candidates up via `onDiscoveredStores`.
 *   - Step 2 reuses `StoresCard` — it receives the discovered candidates via
 *     `discoveredFromLocationSave` and renders the review-and-save checklist,
 *     persisting the pick through `POST /api/household/import-stores`
 *     (`useAddStoresToSelection`). The manager can also "Find nearby stores"
 *     again from inside the card.
 *
 * Both steps are skippable: a busy first run never blocks the app. Location is
 * the step that makes the app useful (a located household sees offers via
 * radius mode), so step 2 is purely optional — the manager can finish setup
 * later from Store preferences. Skipping or finishing lands on `/home`.
 *
 * T30.5 — the wizard carries NO Save button. The reused cards auto-save their
 * editable fields (location commits on blur/Enter; the discovered-store
 * checklist commits on toggle), so the wizard's controls are navigation only:
 * Continue advances, Skip for now / Done defer or finish. This removes the old
 * Save+Continue double-button the reused cards used to add.
 *
 * Routing into this flow is the FirstRunGate's job (FirstRunGate.tsx); this
 * component is the destination. It is mounted OUTSIDE the AppShell so it reads
 * as a focused full-screen setup, not a mode destination — mirroring how the
 * admin console sits inside AuthGuard but outside AppShell (App.tsx).
 */

import { useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useCurrentHousehold,
  useHouseholds,
} from '@shopista/shared/api/hooks';
import type { DiscoveredStore, OnlineStoreCandidate } from '@shopista/shared/types';
import { routes } from '../../routes/paths';
import { Button, Logo, Spinner } from '../../components';
import { LocationCard, StoresCard } from '../settings/cards';
import { markFirstRunDeferred } from './firstRunDeferral';
import { resolveCurrentMembership } from './currentMembership';

const pageStyle: CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
};

// Exported for FirstRunSetup.test.tsx — happy-dom's CSSOM drops the nested
// `env()` / `max()` value when React assigns it via `el.style`, so the test
// asserts on this source object (the safe-area top inset) directly.
export const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  padding: 'var(--space-3) var(--space-6)',
  // T27.2.5.1 (A2) — same status-bar top inset as the AppShell header, so the
  // first-run wizard's logo clears the device clock/battery on an edge-to-edge
  // Android WebView. `max(...)` keeps the normal top padding with no inset
  // (web/desktop); the native safe-area plugin (T27.2.2) populates the real
  // value. This later longhand overrides only the top of the shorthand above.
  paddingTop: 'max(var(--space-3), env(safe-area-inset-top, 0px))',
  borderBottomWidth: '0.0625rem',
  borderBottomStyle: 'solid',
  borderBottomColor: 'var(--color-border-subtle)',
  backgroundColor: 'var(--color-bg-raised)',
};

const mainStyle: CSSProperties = {
  flex: 1,
  width: '100%',
  maxWidth: '40rem',
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingTop: 'var(--space-4)',
  paddingLeft: 'var(--space-6)',
  paddingRight: 'var(--space-6)',
  paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-4)',
};

const stepLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-bold)',
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: 'var(--color-text-muted)',
};

const titleStyle: CSSProperties = {
  // T47 Phase 4 — onboarding step titles → Poppins display.
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-bold)',
  margin: 0,
};

const leadStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  margin: 0,
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-3)',
  marginTop: 'var(--space-2)',
};

type Step = 'location' | 'stores';

export function FirstRunSetup(): JSX.Element {
  const navigate = useNavigate();
  const householdQuery = useCurrentHousehold();
  const householdsQuery = useHouseholds();
  const [step, setStep] = useState<Step>('location');
  // Candidates the location save discovered, handed to StoresCard's checklist.
  const [discovered, setDiscovered] = useState<DiscoveredStore[] | null>(null);
  // BUG57 — the online-store lane from that same location save's PATCH
  // response, handed to StoresCard's online group. Wired here (unlike the
  // stand-alone LocationCard on AppSettingsPage, which has no online-store
  // wiring) so a manager who sets their location in step 1 sees any online
  // candidates in step 2 without an extra "Find nearby stores" click — the
  // backend already resolved them in the same save.
  const [onlineDiscovered, setOnlineDiscovered] = useState<OnlineStoreCandidate[]>([]);

  const household = householdQuery.data;
  const currentMembership = resolveCurrentMembership(
    householdsQuery.data,
    household?.id,
  );
  const isManager = currentMembership?.role === 'manager';
  const hasLocation = household?.latitude != null && household?.longitude != null;
  const hasPostalLocation = Boolean(household?.postal_code?.trim());

  // Skipping or finishing records a device-local deferral so the gate doesn't
  // bounce a manager who skipped the location step straight back into the flow
  // (AC3). A located household never depends on this marker — it short-circuits
  // on the location check; the marker only frees a still-unlocated manager who
  // chose to defer. Setup remains resumable from Settings.
  const finish = () => {
    if (household?.id) markFirstRunDeferred(household.id);
    navigate(routes.home, { replace: true });
  };

  if (householdQuery.isLoading) {
    return (
      <div style={pageStyle} data-testid="first-run-loading">
        <main style={mainStyle}>
          <Spinner />
        </main>
      </div>
    );
  }

  return (
    <div style={pageStyle} data-testid="first-run-setup">
      <header style={headerStyle}>
        <Logo size={28} showText data-testid="first-run-logo" />
      </header>

      <main style={mainStyle}>
        {step === 'location' ? (
          <>
            <div>
              <div style={stepLabelStyle}>Step 1 of 2</div>
              <h1 style={titleStyle}>Where do you shop?</h1>
              <p style={leadStyle}>
                Set your home location so we can show offers from stores near
                you. You can change this any time in Settings.
              </p>
            </div>

            <LocationCard
              household={household}
              isManager={isManager}
              onDiscoveredStores={setDiscovered}
              onOnlineStores={setOnlineDiscovered}
            />

            <div style={actionsStyle}>
              <Button
                variant="ghost"
                size="sm"
                onClick={finish}
                data-testid="first-run-location-skip"
              >
                Skip for now
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={!hasLocation && !hasPostalLocation}
                onClick={() => setStep('stores')}
                data-testid="first-run-location-continue"
              >
                Continue
              </Button>
            </div>
          </>
        ) : (
          <>
            <div>
              <div style={stepLabelStyle}>Step 2 of 2</div>
              <h1 style={titleStyle}>Pick your stores</h1>
              <p style={leadStyle}>
                Choose the stores you shop at to focus offers and pricing on
                them. You can skip this — we'll show offers from nearby stores
                until you do.
              </p>
            </div>

            <StoresCard
              isManager={isManager}
              discoveredFromLocationSave={discovered}
              onConsumeDiscoveredFromLocationSave={() => setDiscovered(null)}
              onlineStoresFromLocationSave={onlineDiscovered}
              // BUG42 — the wizard's primary Done below is this screen's only
              // exit; the card's internal checklist-dismiss "Done" (a Settings
              // control) would be a second same-labeled button that strands the
              // user on a collapsed step.
              hideChecklistDone
            />

            <div style={actionsStyle}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStep('location')}
                data-testid="first-run-stores-back"
              >
                ‹ Back
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={finish}
                data-testid="first-run-stores-done"
              >
                Done
              </Button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
